import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/tryAndBuy/tryAndBuyFBBDialogStyle';
import { BodyMBold, BodyS, BodyM } from 'components/Typography/Typography';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import PlanCard from '../subcomponents/PlanCard';
import ArrowIcon from 'assets/img/icons/arrow.svg';
import DialogTermsAndConditions from 'views/Numbers/Details/Services/subcomponents/common/DialogTermsAndConditions';
import { catchError, finalize } from 'rxjs/operators';
import { ProductsService } from 'services/products/products';
import { SingleCustomerProduct } from 'utils/api/models/products/singleCustomerProduct';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import { concatMap } from 'rxjs/operators';
import { extractAttributeValue } from 'utils/ecommerce/services/extract';
import { EMPTY, throwError } from 'rxjs';
import moment from 'moment';
import { AccountType } from 'utils/api/models/accounts/accountType';
import CustomDropdownWithSearch from 'components/CustomDropdownWithSearch';
import { SearchService } from 'services/search/search';
import { SingleSearchNumber } from 'utils/api/models/search/singleSearchNumber';
import { SinglePackage } from 'utils/api/models/numbers/singlePackage';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import CustomPopUpWithLoader from 'components/CustomPopUpWithLoader/CustomPopUpWithLoader';
import AccountNumber from '../subcomponents/AccountNumber';
import invalidateCache from 'utils/api/invalidateCache';
import { AccountsService } from 'services/accounts/accounts';
import { MAX_ACCOUNT_CATEGORY, MIN_ACCOUNT_CATEGORY } from 'components/TryAndBuy/constants';
import apiConfig from 'utils/api/config';
import { loadQualtrics } from 'utils/qualtrics';

// @ts-ignore
const useStyles = makeStyles(styles);

const MIN_SEARCH_TEXT_LENGTH = 3;
const MAX_NUMBER_OF_SUGGESTIONS = 5;

type State = 'failure' | 'success' | undefined;

interface IFBBDialogProps {
	isOpen: boolean;
	hideDialog: () => void;
	header: string;
	selectedAccount?: {
		accountNum: string;
		tryAndBuyProduct: types.ecommerce.CatalogEntryView;
		currentTariffPlan: SingleCustomerProduct;
	};
	reload?: () => void;
}

const FBBDialog = ({ hideDialog, header, selectedAccount, isOpen, reload }: IFBBDialogProps) => {
	const classes = useStyles();
	const [loadingNumbers, setLoadingNumbers] = useState<boolean>(false);
	const [loadingDetailsForSelectedNumber, setLoadingDetailsForSelectedNumber] = useState<boolean>(false);
	const [subscribingTryAndBuyPackage, setSubscribingTryAndBuyPackage] = useState<boolean>(false);
	const isMounted = useRef(true);
	const [number, setNumber] = useState<string>('');
	const [numbers, setNumbers] = useState<SingleSearchNumber[]>([]);
	const [isChecked, setIsChecked] = useState<boolean>(false);
	const [currentTariffPlan, setCurrentTariffPlan] = useState<SingleCustomerProduct | undefined>();
	const [tryAndBuyProduct, setTryAndBuyProduct] = useState<types.ecommerce.CatalogEntryView | undefined>();
	const [tryAndBuyPackages, setTryAndBuyPackages] = useState<SinglePackage[]>([]);
	const [popUpState, setPopUpState] = useState<State>(undefined);
	const [popUpOpen, setPopUpOpen] = useState<boolean>(false);
	const [tryAndBuyError, setTryAndBuyError] = useState<
		'ALREADY_ACTIVATED' | 'INVALID_ACCOUNT_CATEGORY' | 'HIGHEST_PLAN_ACTIVE' | undefined
	>();

	const calculatedAccountNum = useMemo(() => {
		return selectedAccount ? selectedAccount.accountNum : number;
	}, [selectedAccount, number]);

	const calculatedCurrentTariffPlan = useMemo(() => {
		return selectedAccount ? selectedAccount.currentTariffPlan : currentTariffPlan;
	}, [selectedAccount, currentTariffPlan]);

	const calculatedTryAndBuyProduct = useMemo(() => {
		return selectedAccount ? selectedAccount.tryAndBuyProduct : tryAndBuyProduct;
	}, [selectedAccount, tryAndBuyProduct]);

	const suggestions: types.troubleTicketing.ISingleSuggestion[] = useMemo(() => {
		return numbers.map((singleNumber) => ({
			name: singleNumber.accountNum,
			value: singleNumber.accountType,
		}));
	}, [numbers]);

	const calculatedTryAndBuyPackage = useMemo(() => {
		if (calculatedCurrentTariffPlan) {
			return tryAndBuyPackages.find(
				(singlePackage: SinglePackage) =>
					singlePackage.requiredProducts.indexOf(calculatedCurrentTariffPlan.productId) >= 0
			);
		}
		return undefined;
	}, [tryAndBuyPackages, calculatedCurrentTariffPlan]);

	const isButtonDisabled =
		!isChecked || !calculatedAccountNum || !calculatedCurrentTariffPlan || !calculatedTryAndBuyProduct;

	const helperText: string | undefined = useMemo(() => {
		switch (tryAndBuyError) {
			case 'ALREADY_ACTIVATED':
				return translate('tryandbuy.fbb.dialog.fbbPlan.choose.number.helperText');
			case 'INVALID_ACCOUNT_CATEGORY':
				return translate('tryandbuy.fbb.dialog.fbbPlan.choose.number.helperText.invalidAccountCategory');
			case 'HIGHEST_PLAN_ACTIVE':
				return translate('tryandbuy.fbb.dialog.fbbPlan.choose.number.helperText.highestPlanActive');
			default:
				return undefined;
		}
	}, [tryAndBuyError]);

	const onSuggestionClick = useCallback(
		(suggestion: types.troubleTicketing.ISingleSuggestion) => {
			const _numberInfo = numbers.find(
				({ accountNum, accountType }) => accountNum === suggestion.name && accountType === suggestion.value
			);
			if (_numberInfo) {
				setLoadingDetailsForSelectedNumber(true);
				const getCustomerProductsResponse = AccountsService.getCustomerAccountOperation({
					phoneNumber: _numberInfo.accountNum,
					accountType: _numberInfo.accountType,
				}).pipe(
					concatMap((customerAccountResponse) => {
						const accountCategory = (
							customerAccountResponse.customerAccount?.customer?.customerAccount?.customer
								?.characteristic || []
						).find((c) => c.name === 'ACCOUNT_CATEGORY')?.value;
						const accountCategoryValue = Number(accountCategory);
						if (
							accountCategoryValue >= MIN_ACCOUNT_CATEGORY &&
							accountCategoryValue <= MAX_ACCOUNT_CATEGORY
						) {
							return ProductsService.getCustomerProducts({
								phoneNumber: _numberInfo.accountNum,
								accountType: _numberInfo.accountType,
								billingType: _numberInfo.billingType,
							});
						}
						setTryAndBuyError('INVALID_ACCOUNT_CATEGORY');
						setLoadingDetailsForSelectedNumber(false);
						return EMPTY;
					}),
					catchError((error) => {
						setLoadingDetailsForSelectedNumber(false);
						return throwError(error);
					})
				);
				const getTariffPlanDetails = getCustomerProductsResponse.pipe(
					concatMap(({ tariffPlan, tryAndBuyProduct }) => {
						const _tariffPlan = tariffPlan(_numberInfo.accountType as AccountType);
						if (_tariffPlan && !tryAndBuyProduct) {
							setCurrentTariffPlan(_tariffPlan);
							return ECommerceService.getContentByPartNumber(_tariffPlan.productId || '');
						}
						if (tryAndBuyProduct) {
							setTryAndBuyError('ALREADY_ACTIVATED');
						}
						setLoadingDetailsForSelectedNumber(false);
						return EMPTY;
					}),
					catchError((error) => {
						setLoadingDetailsForSelectedNumber(false);
						return throwError(error);
					})
				);
				const getTryAndBuyDetails = getTariffPlanDetails.pipe(
					concatMap(({ response }) => {
						if (response?.catalogEntryView?.length > 0) {
							const { attributes } = response.catalogEntryView[0];
							const tryAndBuyAttributeValue = extractAttributeValue({ attributes }, 'TRY_AND_BUY');
							if (!tryAndBuyAttributeValue) {
								setTryAndBuyError('HIGHEST_PLAN_ACTIVE');
							}
							return ECommerceService.getContentByPartNumber(tryAndBuyAttributeValue);
						}
						setLoadingDetailsForSelectedNumber(false);
						return EMPTY;
					}),
					catchError((error) => {
						setLoadingDetailsForSelectedNumber(false);
						return throwError(error);
					})
				);
				getTryAndBuyDetails
					.pipe(
						finalize(() => {
							setLoadingDetailsForSelectedNumber(false);
						})
					)
					.subscribe(({ response }) => {
						if (response?.catalogEntryView?.length > 0) {
							setTryAndBuyProduct(response.catalogEntryView[0]);
						}
					});
			}
		},
		[numbers]
	);

	const getNumbers = useCallback(() => {
		setLoadingNumbers(true);
		SearchService.searchNumbersAndUsersRestPost(number)
			.pipe(finalize(() => setLoadingNumbers(false)))
			.subscribe(({ numbers }) =>
				setNumbers(
					numbers
						.filter(({ accountType }) => accountType === AccountType.INTERNET)
						.slice(0, MAX_NUMBER_OF_SUGGESTIONS - 1)
				)
			);
	}, [number]);

	const clearSearch = () => {
		setCurrentTariffPlan(undefined);
		setTryAndBuyProduct(undefined);
		setTryAndBuyError(undefined);
	};

	const handleSubscribe = () => {
		if (calculatedAccountNum && calculatedTryAndBuyPackage) {
			const orderItems: types.services.IServiceItem[] = [
				{
					action: 'ADD',
					productIds: [calculatedTryAndBuyPackage.partNumber],
				},
			];
			setSubscribingTryAndBuyPackage(true);
			hideDialog();
			setPopUpOpen(true);
			ProductsService.submitServices({
				phoneNumber: calculatedAccountNum,
				ebuConfigProduct: calculatedTryAndBuyPackage.ebuConfigProduct,
				orderItems,
			})
				.pipe(
					finalize(() => {
						setSubscribingTryAndBuyPackage(false);
					})
				)
				.subscribe(
					() => {
						setPopUpState('success');
						invalidateCache(['getCorpUserDepartmentDataForNumber', 'getCustomerProducts'], true);
						if (reload) {
							reload();
						}
					},
					() => {
						setPopUpState('failure');
					}
				);
		}
	};

	useEffect(() => {
		if (number.length >= MIN_SEARCH_TEXT_LENGTH) {
			getNumbers();
		} else {
			setNumbers([]);
		}
	}, [number, getNumbers]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		ECommerceService.getPackages('TRY_AND_BUY', 'FBB').subscribe(({ packages }) => {
			setTryAndBuyPackages(packages);
		});
	}, []);

	return (
		<>
			<CustomDialog
				hideDialog={hideDialog}
				width={700}
				isOpen={isOpen}
				header={header}
				id="try-now-dialog"
				dialogBody={
					<div className={classes.wrapper}>
						<div className={classes.fbbDescriptionWrapper}>
							<BodyM disableMargin>
								<span
									dangerouslySetInnerHTML={{ __html: translate('tryandbuy.fbb.dialog.description1') }}
								></span>
							</BodyM>
							<BodyM disableMargin>
								<span
									className="mt"
									dangerouslySetInnerHTML={{
										__html: translate(
											'tryandbuy.fbb.dialog.description2',
											apiConfig.links.fbbPackages
										),
									}}
								></span>
							</BodyM>
						</div>

						<div className={classes.chooseNumber}>
							<ConditionalRender
								show={!!selectedAccount}
								onTrue={() => <AccountNumber number={selectedAccount?.accountNum} />}
								onFalse={() => (
									<CustomDropdownWithSearch
										label={translate('tryandbuy.fbb.dialog.fbbPlan.choose.number')}
										value={number}
										setValue={setNumber}
										suggestions={suggestions}
										loading={loadingNumbers}
										minSearchTextLength={MIN_SEARCH_TEXT_LENGTH}
										onClick={onSuggestionClick}
										clear={clearSearch}
										selected={!!currentTariffPlan && !!tryAndBuyProduct}
										error={!!tryAndBuyError}
										helperText={helperText}
									/>
								)}
							/>
						</div>

						<ConditionalRender
							show={loadingDetailsForSelectedNumber}
							onTrue={() => <CustomCircularProgress transparent text={''} loading />}
						/>

						<ConditionalRender
							show={!!calculatedCurrentTariffPlan && !!calculatedTryAndBuyProduct}
							onTrue={() => (
								<div className={classes.fbbPlanSectionWrapper}>
									<BodyMBold disableMargin>{translate('tryandbuy.fbb.dialog.fbbPlan')}</BodyMBold>
									<div className={classes.fbbNewCurrenPlanWrapper}>
										<PlanCard
											planType={translate('tryandbuy.fbb.dialog.fbbPlan.currentPlan')}
											planName={calculatedCurrentTariffPlan?.name || ''}
										/>
										<img alt="arrow icon" src={ArrowIcon} className={classes.arrow} />
										<PlanCard
											planType={translate('dashboard.try.and.buy')}
											planName={calculatedTryAndBuyProduct?.name || ''}
											newPlan={true}
										/>
									</div>
									<BodyS color="primary-light" disableMargin>
										{translate('tryandbuy.fbb.dialog.fbbPlan.offer.end') +
											moment()
												.add(calculatedTryAndBuyPackage?.duration, 'months')
												.format('DD.MM.YYYY')}
									</BodyS>
									<div className={classes.fbbPolicyWrapper}>
										<BodyMBold disableMargin>
											{translate('tryandbuy.fbb.dialog.termsAndConfitions')}
										</BodyMBold>
										<BodyS disableMargin>{translate('tryandbuy.fbb.dialog.policy.2')}</BodyS>
										<BodyS disableMargin>{translate('tryandbuy.fbb.dialog.policy.3')}</BodyS>
										<BodyS disableMargin>{translate('tryandbuy.fbb.dialog.policy.4')}</BodyS>
										<BodyS disableMargin>{translate('tryandbuy.fbb.dialog.policy.5')}</BodyS>
										<BodyS disableMargin>{translate('tryandbuy.fbb.dialog.policy.6')}</BodyS>
									</div>
									<div>
										<DialogTermsAndConditions
											isChecked={isChecked}
											showCheckbox
											showError={false}
											setIsChecked={setIsChecked}
										/>
									</div>
								</div>
							)}
						/>
					</div>
				}
				rightSideButtons={[
					{
						color: 'tertiary-light',
						text: translate('cancel'),
						onClick: hideDialog,
					},
					{
						color: 'primary-light',
						text: translate('tryandbuy.tryNow'),
						onClick: handleSubscribe,
						disabled: isButtonDisabled,
					},
				]}
			/>
			<CustomPopUpWithLoader
				isOpen={popUpOpen}
				hideDialog={() => {
					setPopUpOpen(false);
					loadQualtrics({ activity_type: 'Try and Buy' });
				}}
				loading={subscribingTryAndBuyPackage}
				isError={popUpState === 'failure'}
				header={
					popUpState === 'failure'
						? [translate('custom.pop.up.failure.header'), translate('custom.pop.up.failure.header2')]
						: [translate('custom.pop.up.success.header'), translate('custom.pop.up.success.header2')]
				}
			/>
		</>
	);
};

export default FBBDialog;
